import { FC, useState } from "react";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Form, Formik, FormikConfig, useField } from "formik";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { useDeleteClientMutation } from "@api/organizationClient.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { Organization } from "@common/types/organization";
import styles from "../ClientRemoval.module.scss";
import actionsStyles from "../../ClientActions.module.scss";
import { ClientDetailsRow } from "../../views/clientViewConfig";

interface Props {
    client: ClientDetailsRow;
    onClose: () => void;
}

interface DeleteClientFormSchema {
    clientName: string;
    confirmDeletion: boolean;
}

const fields = createFormikFields<DeleteClientFormSchema>();

const ConfirmationCheckbox: FC = () => {
    const [field, , helpers] = useField<boolean>("confirmDeletion");

    return (
        <Checkbox
            checked={field.value}
            onCheckedChange={helpers.setValue}
            name={field.name}
            label="I understand this will remove the client and all of its historical data."
            className={styles.confirmationCheckbox}
        />
    );
};

export const DeleteClientModal: FC<Props> = ({ client, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const activeOrganization = useActiveOrganization() as Organization;
    const deleteClientMutation = useDeleteClientMutation();
    const { toast } = useToaster();
    const form: FormikConfig<DeleteClientFormSchema> = {
        initialValues: {
            clientName: "",
            confirmDeletion: false,
        },
        validate: (values) => {
            const errors: Record<string, string> = {};

            if (values.clientName !== client.name) {
                errors.clientName = "Client name is incorrect";
            }

            if (!values.confirmDeletion) {
                errors.confirmDeletion = "You must confirm to proceed";
            }

            return errors;
        },
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async () => {
                await deleteClientMutation.mutateAsync({
                    organizationId: activeOrganization.id,
                    clientId: client.id,
                });

                toast("Client deleted");

                onClose();
            },
        }),
    };

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Remove client and delete data?
            </StandardModalHeader>
            <StandardModalBody className={actionsStyles.modalBody}>
                <Formik {...form}>
                    {({ isValid }) => (
                        <Form>
                            <section>
                                <p>
                                    You're the only member with access to this
                                    client, therefore:{" "}
                                </p>

                                <ul>
                                    <li>All data will be deleted from Kick</li>
                                    <li>
                                        You’ll lose access to their workspace
                                        and books
                                    </li>
                                </ul>

                                <p>
                                    To confirm, enter the client name (
                                    {client.name}) below.
                                </p>
                            </section>

                            <fields.Text
                                name="clientName"
                                label="Client name"
                                size="xl"
                                className={styles.inputField}
                            />

                            <ConfirmationCheckbox />

                            <p className="text-danger">
                                Warning: This action cannot be undone.
                            </p>

                            <footer className={styles.footer}>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={loading}
                                    size="xl"
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    type="submit"
                                    variant="destructive"
                                    disabled={!isValid || loading}
                                    loading={loading}
                                    size="xl"
                                >
                                    Yes, remove and delete data
                                </ButtonWithLoader>
                            </footer>
                        </Form>
                    )}
                </Formik>
            </StandardModalBody>
        </>
    );
};
